/**
 * Typed calls against the Natura Tagger SKU catalog endpoints.
 * The catalog is global per tenant; `code` is what we persist as
 * `class_label` on cycle objects.
 */
import { apiFetch } from "./client";

export type Sku = { code: string; name: string };

export type SkuPage = {
  items: Sku[];
  nextCursor: string | null;
};

/**
 * Bulk fetch of the SKU catalog used by SkuPicker (loaded once + filtered
 * client-side). Bumps the limit so even a 24K-row catalog returns in a
 * single round trip; if the catalog ever grows past this, switch the
 * picker to useInfiniteQuery as well.
 */
export async function getSkus(q?: string): Promise<Sku[]> {
  const params = new URLSearchParams({ limit: "50000" });
  if (q) params.set("q", q);
  const body = await apiFetch<SkuPage>(`/v1/skus?${params}`);
  return body.items;
}

/** Cursor-paginated fetch for the /skus management page. */
export async function getSkusPage(args: {
  q?: string;
  cursor?: string | null;
  limit?: number;
}): Promise<SkuPage> {
  const params = new URLSearchParams({
    limit: String(args.limit ?? 50),
  });
  if (args.q) params.set("q", args.q);
  if (args.cursor) params.set("cursor", args.cursor);
  return apiFetch<SkuPage>(`/v1/skus?${params}`);
}

export type SkuImportResult = {
  total: number;
  created: number;
  updated: number;
  unchanged: number;
};

export async function importSkus(file: File): Promise<SkuImportResult> {
  const fd = new FormData();
  fd.append("file", file);
  return apiFetch<SkuImportResult>("/v1/skus/import", {
    method: "POST",
    body: fd,
  });
}

// `name` is optional (Feedback V2 rev02 r2-13: the product name isn't used in
// this flow, so the SKU screen only manages codes). Defaults to "".
export async function createSku(code: string, name = ""): Promise<Sku> {
  return apiFetch<Sku>("/v1/skus", {
    method: "POST",
    body: JSON.stringify({ code, name }),
  });
}

export async function updateSku(code: string, name: string): Promise<Sku> {
  return apiFetch<Sku>(`/v1/skus/${encodeURIComponent(code)}`, {
    method: "PATCH",
    body: JSON.stringify({ name }),
  });
}

export async function deleteSku(code: string): Promise<void> {
  await apiFetch<void>(`/v1/skus/${encodeURIComponent(code)}`, {
    method: "DELETE",
  });
}
